import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { STRING_MENU } from "../../../constants/string";
import { toggleMenuContent } from "../../../store/themeConfigSlice";
import { MODULE_ROUTE } from "../../../routers/module-router";
import QuickActions from "../components/quick-actions";
import RecentOrders from "../components/recent-orders";
import { RevenueTimeReport } from "../components/revenue-time-report";
import { DashboardPanel } from "../components/dashboard-panel";
import OrderService from "../../order-list/services/order-service";

// Icons
const TrendUpIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const TrendDownIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
  </svg>
);

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: { value: number; isPositive: boolean };
  subtitle?: string;
  color: "green" | "blue" | "purple" | "orange" | "red" | "teal";
  loading?: boolean;
}

const colorVariants = {
  green: {
    bg: "bg-gradient-to-br from-emerald-500 to-green-600",
    light: "bg-emerald-50",
    text: "text-emerald-600",
    icon: "bg-white/20",
  },
  blue: {
    bg: "bg-gradient-to-br from-blue-500 to-indigo-600",
    light: "bg-blue-50",
    text: "text-blue-600",
    icon: "bg-white/20",
  },
  purple: {
    bg: "bg-gradient-to-br from-purple-500 to-violet-600",
    light: "bg-purple-50",
    text: "text-purple-600",
    icon: "bg-white/20",
  },
  orange: {
    bg: "bg-gradient-to-br from-orange-500 to-amber-600",
    light: "bg-orange-50",
    text: "text-orange-600",
    icon: "bg-white/20",
  },
  red: {
    bg: "bg-gradient-to-br from-red-500 to-rose-600",
    light: "bg-red-50",
    text: "text-red-600",
    icon: "bg-white/20",
  },
  teal: {
    bg: "bg-gradient-to-br from-teal-500 to-cyan-600",
    light: "bg-teal-50",
    text: "text-teal-600",
    icon: "bg-white/20",
  },
};

const StatCard = ({ title, value, icon, trend, subtitle, color, loading }: StatCardProps) => {
  const variant = colorVariants[color];

  if (loading) {
    return (
      <div className={`${variant.bg} rounded-2xl p-6 text-white shadow-lg animate-pulse`}>
        <div className="flex items-start justify-between">
          <div className="space-y-3 flex-1">
            <div className="h-4 bg-white/30 rounded w-24"></div>
            <div className="h-8 bg-white/30 rounded w-32"></div>
            <div className="h-3 bg-white/30 rounded w-20"></div>
          </div>
          <div className={`${variant.icon} p-3 rounded-xl`}>
            <div className="w-6 h-6 bg-white/30 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${variant.bg} rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-white/80">{title}</p>
          <p className="text-3xl font-bold mt-2">{value}</p>
          {trend && (
            <div className="flex items-center gap-1 mt-2">
              <span className={`flex items-center gap-0.5 text-sm font-medium ${
                trend.isPositive ? "text-green-200" : "text-red-200"
              }`}>
                {trend.isPositive ? <TrendUpIcon /> : <TrendDownIcon />}
                {trend.value}%
              </span>
              {subtitle && <span className="text-white/60 text-sm">{subtitle}</span>}
            </div>
          )}
          {!trend && subtitle && (
            <p className="text-white/60 text-sm mt-2">{subtitle}</p>
          )}
        </div>
        <div className={`${variant.icon} p-3 rounded-xl`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

// Quick Link Card
interface QuickLinkProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  to: string;
  color: string;
}

const QuickLinkCard = ({ title, description, icon, to, color }: QuickLinkProps) => (
  <Link
    to={to}
    className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all group"
  >
    <div className={`${color} p-3 rounded-xl text-white group-hover:scale-110 transition-transform`}>
      {icon}
    </div>
    <div>
      <h4 className="font-semibold text-gray-900">{title}</h4>
      <p className="text-sm text-gray-500">{description}</p>
    </div>
    <svg className="w-5 h-5 text-gray-400 ml-auto group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  </Link>
);

export const DashboardPage = () => {
  const dispatch = useDispatch();
  useEffect(() => { dispatch(toggleMenuContent(STRING_MENU.DASHBOARD)); }, [dispatch]);

  const [metrics, setMetrics] = useState({
    ordersToday: 0,
    revenueToday: 0,
    ordersYesterday: 0,
    revenueYesterday: 0,
  });
  const [loading, setLoading] = useState(true);

  const { fetch: listOrders } = OrderService.list();
  const listOrdersRef = useRef(listOrders);
  useEffect(() => { listOrdersRef.current = listOrders; }, [listOrders]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const formatDate = (date: Date) => {
          const y = date.getFullYear();
          const m = String(date.getMonth() + 1).padStart(2, "0");
          const d = String(date.getDate()).padStart(2, "0");
          return `${y}-${m}-${d}`;
        };

        const todayStr = formatDate(today);
        const yesterdayStr = formatDate(yesterday);

        // Get today's orders
        const resToday = await listOrdersRef.current({ page: 1, limit: 1, from_date: todayStr, to_date: todayStr, statuses: 5 });
        const ordersToday = Number((resToday?.data as any)?.totalRecord ?? (resToday?.data as any)?.total_record ?? 0);

        // Get yesterday's orders
        const resYesterday = await listOrdersRef.current({ page: 1, limit: 1, from_date: yesterdayStr, to_date: yesterdayStr, statuses: 5 });
        const ordersYesterday = Number((resYesterday?.data as any)?.totalRecord ?? (resYesterday?.data as any)?.total_record ?? 0);

        // Calculate revenue
        const PAGE_LIMIT = 500;
        let page = 1;
        let revenueToday = 0;
        let totalRecords = 0;
        do {
          const resPage: any = await listOrdersRef.current({ page, limit: PAGE_LIMIT, statuses: 5, from_date: todayStr, to_date: todayStr });
          const data: any = resPage?.data ?? {};
          const list: any[] = Array.isArray(data.list) ? data.list : [];
          revenueToday += list.reduce((sum, o) => sum + Number(o?.total_amount ?? 0), 0);
          totalRecords = Number(data.totalRecord ?? data.total_record ?? data.total ?? list.length ?? 0);
          page += 1;
          if (list.length < PAGE_LIMIT) break;
        } while ((page - 1) * PAGE_LIMIT < totalRecords);

        // Get yesterday's revenue
        page = 1;
        let revenueYesterday = 0;
        do {
          const resPage: any = await listOrdersRef.current({ page, limit: PAGE_LIMIT, statuses: 5, from_date: yesterdayStr, to_date: yesterdayStr });
          const data: any = resPage?.data ?? {};
          const list: any[] = Array.isArray(data.list) ? data.list : [];
          revenueYesterday += list.reduce((sum, o) => sum + Number(o?.total_amount ?? 0), 0);
          totalRecords = Number(data.totalRecord ?? data.total_record ?? data.total ?? list.length ?? 0);
          page += 1;
          if (list.length < PAGE_LIMIT) break;
        } while ((page - 1) * PAGE_LIMIT < totalRecords);

        if (mounted) {
          setMetrics({ ordersToday, revenueToday, ordersYesterday, revenueYesterday });
          setLoading(false);
        }
      } catch (error) {
        console.error("Failed to fetch dashboard metrics:", error);
        if (mounted) {
          setMetrics({ ordersToday: 0, revenueToday: 0, ordersYesterday: 0, revenueYesterday: 0 });
          setLoading(false);
        }
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Calculate trends
  const ordersTrend = metrics.ordersYesterday > 0
    ? ((metrics.ordersToday - metrics.ordersYesterday) / metrics.ordersYesterday * 100).toFixed(1)
    : 0;
  const revenueTrend = metrics.revenueYesterday > 0
    ? ((metrics.revenueToday - metrics.revenueYesterday) / metrics.revenueYesterday * 100).toFixed(1)
    : 0;

  // Format currency
  const formatCurrency = (value: number) => {
    if (value >= 1000000000) return `${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value.toLocaleString("vi-VN");
  };

  // Get greeting based on time
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Chào buổi sáng";
    if (hour < 18) return "Chào buổi chiều";
    return "Chào buổi tối";
  };

  // Format date
  const formatCurrentDate = () => {
    return new Date().toLocaleDateString("vi-VN", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen relative z-0">
      {/* Header */}
      <header className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{getGreeting()}!</h1>
            <p className="text-gray-500 mt-1">{formatCurrentDate()}</p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to={MODULE_ROUTE.POS_ORDER}
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-medium shadow-lg shadow-emerald-200"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Tạo đơn mới
            </Link>
            <Link
              to={MODULE_ROUTE.REPORT_EXECUTIVE}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Xem báo cáo
            </Link>
          </div>
        </div>
      </header>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Đơn hàng hôm nay"
          value={metrics.ordersToday}
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          }
          trend={{ value: Number(ordersTrend), isPositive: Number(ordersTrend) >= 0 }}
          subtitle="so với hôm qua"
          color="purple"
          loading={loading}
        />
        <StatCard
          title="Doanh thu hôm nay"
          value={`${formatCurrency(metrics.revenueToday)}₫`}
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          trend={{ value: Number(revenueTrend), isPositive: Number(revenueTrend) >= 0 }}
          subtitle="so với hôm qua"
          color="green"
          loading={loading}
        />
        <StatCard
          title="Giá trị TB/đơn"
          value={metrics.ordersToday > 0 ? `${formatCurrency(metrics.revenueToday / metrics.ordersToday)}₫` : "0₫"}
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
            </svg>
          }
          subtitle="Trung bình giá trị đơn"
          color="blue"
          loading={loading}
        />
        <StatCard
          title="Đang xử lý"
          value={0}
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          subtitle="Đơn đang chờ xử lý"
          color="orange"
          loading={loading}
        />
      </div>

      {/* Quick Access Links */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <QuickLinkCard
          title="POS Bán hàng"
          description="Tạo đơn hàng mới"
          to={MODULE_ROUTE.POS_ORDER}
          icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" /></svg>}
          color="bg-emerald-500"
        />
        <QuickLinkCard
          title="Danh sách đơn"
          description="Quản lý đơn hàng"
          to={MODULE_ROUTE.ORDER}
          icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>}
          color="bg-blue-500"
        />
        <QuickLinkCard
          title="Khách hàng"
          description="Quản lý khách hàng"
          to={MODULE_ROUTE.CUSTOMER}
          icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>}
          color="bg-purple-500"
        />
        <QuickLinkCard
          title="Báo cáo"
          description="Xem báo cáo chi tiết"
          to={MODULE_ROUTE.REPORT_EXECUTIVE}
          icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>}
          color="bg-orange-500"
        />
      </div>

      {/* Revenue Chart */}
      <div className="mb-8">
        <DashboardPanel title="Báo cáo doanh thu theo thời gian">
          <RevenueTimeReport />
        </DashboardPanel>
      </div>

      {/* Bottom Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Orders */}
        <div className="lg:col-span-2">
          <DashboardPanel
            title="Đơn hàng gần đây"
            action={
              <Link to={MODULE_ROUTE.ORDER} className="text-sm text-emerald-600 hover:text-emerald-700 font-medium">
                Xem tất cả →
              </Link>
            }
          >
            <RecentOrders />
          </DashboardPanel>
        </div>

        {/* Quick Actions */}
        <div className="lg:col-span-1">
          <DashboardPanel title="Thao tác nhanh">
            <QuickActions />
          </DashboardPanel>
        </div>
      </div>
    </div>
  );
};
